/**
 * Atribuição de custo de mapas: QUEM chamou o mp-geospatial.
 *
 * O mp-geospatial grava uma linha por requisição em `geo_cost.geo_requests`, com o campo `caller`
 * (vem do cabeçalho `X-MP-Caller`, que a API repassa). Sem o cabeçalho, ~92% das chamadas pagas de
 * geocode/reverse ficavam sem origem — não dava para dizer quanto do custo é do pedir.delivery. Este
 * módulo decide QUANDO e PARA ONDE o app manda os rótulos; quem os carimba é o `GeoCallerInterceptor`.
 *
 * ## O que sai
 *
 *   X-MP-Caller: pedir-address   captura de endereço (autocomplete, forward/reverse-geocode, Photon)
 *   X-MP-Caller: pedir-fee       cálculo de frete (POST /restaurant/deliveryfees, frete por coordenada)
 *   X-Correlation-Id: <uuid>     só nas de endereço: o `correlation_id` da sessão de captura
 *                                (`AddressCaptureSessionService`), o MESMO que a telemetria multissinal
 *                                grava em `multisignal_telemetry_prod.traces`. É o que junta o custo
 *                                (geo_cost) com o percurso do cliente por sessão. Sem sessão (id vazio ou
 *                                fora do formato UUID), vai só o rótulo.
 *
 * O backend valida o rótulo com `^[a-z0-9_-]{1,32}$`; os dois rótulos acima passam (há teste).
 * Sem PII em nenhum dos cabeçalhos: só rótulo e uuid.
 *
 * ## As quatro travas (é o caminho de receita do cliente final)
 *
 * 1. **Allowlist de HOST** (`GEO_CALLER_CONFIG.hosts`): só a API própria, comparação EXATA do hostname,
 *    https, porta padrão, sem userinfo. Nunca terceiros (Google, ViaCEP, Photon público, Lambdas de
 *    telemetria), nem "tudo que começa por api". Cada host da lista foi conferido com um preflight
 *    real: o navegador BLOQUEIA a chamada inteira se o `Access-Control-Allow-Headers` do host não
 *    aceitar o cabeçalho — e aqui isso derrubaria o frete e o autocomplete do checkout.
 *    `green.multipedidos.com.br` (que serve green/blue/yellow) NÃO está na lista: ele só declara
 *    `*, authorization` (curinga), sem listar os cabeçalhos por nome. Em Chrome 149 o curinga passa,
 *    mas só entra na lista quando o Apache dele os listar como o de produção.
 * 2. **Dial por loja** (`GEO_CALLER_CONFIG.rolloutPercent`, default 10): `restaurant_id % 100 < N`.
 *    Determinístico por loja — a mesma loja entra ou sai sempre, e uma sessão nunca fica pela metade.
 *    `0` desliga tudo; valor fora de 0–100 também (falha para o lado seguro). Subir exige novo deploy; só suba depois de conferir em `geo_cost.geo_requests`
 *    que as lojas dentro do dial aparecem com `caller` preenchido e que o autocomplete delas não piorou.
 * 3. **Desligador por aparelho**: `localStorage.pedirCallerHeader = "off"` (suporte). `"on"` ignora o
 *    dial (QA numa loja fora dos 10%) — NÃO ignora a allowlist de host.
 * 4. **Nunca lança, nunca atrasa**: o interceptor tem try/catch total. Falha = a requisição segue como
 *    sempre, sem o cabeçalho.
 *
 * Fora do dial, desligado ou em host/rota fora da lista, a requisição sai BYTE A BYTE como antes (o
 * interceptor devolve o MESMO objeto, sem clonar).
 *
 * ## O custo que isto traz: PREFLIGHT novo nas GET
 *
 * Cabeçalho customizado transforma a requisição "simples" em uma com preflight (OPTIONS). Quem já
 * paga o preflight hoje não sente diferença: toda requisição com `Authorization` (cliente logado — o
 * TokenInterceptor o carimba) e todo POST/PUT com corpo JSON (`Content-Type: application/json`).
 * Quem passa a pagar é o **cliente anônimo** nas GET de endereço e de frete por coordenada.
 *
 * ⚠️ O cache do preflight (`Access-Control-Max-Age: 86400`) vale por **URL completa, query string
 * inclusa** — medido no Chrome 149: mesma URL 5x = 1 OPTIONS; mesmo caminho com 5 `query`
 * diferentes = 5 OPTIONS. Ou seja, "só a 1ª chamada de cada URL" na prática é QUASE TODA chamada de
 * autocomplete/reverse/forward (cada uma tem `query` ou `lat`/`lon` próprios). O OPTIONS de
 * `api.multipedidos.com.br` não é cacheado pela CloudFront (`Miss from cloudfront`): vai até a origem e
 * custou ~170 ms (mediana, conexão já aberta, medido do Brasil) — um round trip inteiro, igual ao de
 * uma GET 404 na mesma conexão (~190 ms). É por isso que o dial default é 10 e não 100: a decisão de
 * subir deve olhar a latência do autocomplete nas lojas dentro do dial.
 *
 * Detalhes, a tabela de preflight por host e como consultar: `ATRIBUICAO-CUSTO-GEO.md`.
 */

/** Rótulos aceitos pelo backend (`^[a-z0-9_-]{1,32}$`). */
export type GeoCaller = 'pedir-address' | 'pedir-fee';

export const GEO_CALLER_HEADER = 'X-MP-Caller';
export const GEO_CORRELATION_HEADER = 'X-Correlation-Id';

/** `localStorage`: `"off"` desliga neste aparelho; `"on"` ignora o dial (QA). Qualquer outro valor = nada. */
export const GEO_CALLER_SWITCH_KEY = 'pedirCallerHeader';

/**
 * Configuração de rollout. Fica num objeto, e não em constantes soltas, para os testes mexerem nela
 * (mesmo desenho do `pdvTelemetryLogger.config` do app-client).
 *
 * - `rolloutPercent`: fração das lojas que mandam os cabeçalhos (`restaurant_id % 100 < N`). 0 desliga
 *   todas; 100 liga todas.
 * - `hosts`: hostnames da API própria que ACEITAM os três cabeçalhos no preflight. Conferido em
 *   2026-10-07, por OPTIONS real nas 11 rotas deste módulo: `api.multipedidos.com.br` lista
 *   `x-correlation-id, x-request-id, x-mp-caller` por nome.
 */
export const GEO_CALLER_CONFIG: { rolloutPercent: number; hosts: string[] } = {
    rolloutPercent: 10,
    hosts: ['api.multipedidos.com.br'],
};

export interface GeoCallerRule {
    method: string;
    /** Casa com o `pathname` (sem query string). */
    path: RegExp;
    caller: GeoCaller;
    /** Manda também o `X-Correlation-Id` da sessão de captura? Só as chamadas de endereço. */
    correlation: boolean;
}

/**
 * As rotas que levam rótulo. É uma lista fechada de propósito: rota nova do pedir.delivery não ganha
 * cabeçalho (e, portanto, preflight) sem alguém decidir isso aqui.
 *
 * Ficam DE FORA, de propósito:
 *  - `GET /restaurant/{id}/geospatial/free-pin`: Photon, sem custo de mapa — só ganharia preflight.
 *  - `PUT /restaurant/deliveryfees` (lista de bairros), `POST /deliveryfees/discount/by-value` e
 *    `POST /bee/regions`: não geram chamada de mapa.
 */
export const GEO_CALLER_RULES: ReadonlyArray<GeoCallerRule> = [
    // Captura de endereço — tela multissinal e alta precisão (GET: sem Authorization, passam a ter preflight).
    { method: 'GET', path: /^\/restaurant\/\d+\/geospatial\/(autocomplete|forward-geocode|reverse-geocode|geocode)\/?$/, caller: 'pedir-address', correlation: true },
    // Captura de endereço — telas antigas (POST com JSON: já tinham preflight, nada muda na latência).
    { method: 'POST', path: /^\/restaurant\/\d+\/photon\/(v2\/geocode|geocodeHereMaps)\/?$/, caller: 'pedir-address', correlation: true },
    // Frete.
    { method: 'POST', path: /^\/restaurant\/deliveryfees\/?$/, caller: 'pedir-fee', correlation: false },
    { method: 'GET', path: /^\/restaurant\/\d+\/geospatial\/deliveryfee\/latitude\/[^/]+\/longitude\/[^/]+\/?$/, caller: 'pedir-fee', correlation: false },
];

/**
 * A requisição é para a API própria (host da allowlist) E é uma rota que leva rótulo? Devolve a regra
 * ou `null`. Barato (um `new URL` e algumas regex) porque roda em TODA requisição do HttpClient.
 */
export function matchGeoCallerRule(method: string, url: string, hosts: string[] = GEO_CALLER_CONFIG.hosts): GeoCallerRule | null {
    let alvo: URL;
    try {
        alvo = new URL(url);
    } catch (e) {
        return null; // URL relativa (assets, i18n) ou inválida: nunca é a API
    }
    // https, porta padrão, sem userinfo: `https://api.multipedidos.com.br@evil.com/` tem hostname evil.com,
    // mas fechar a porta e o userinfo também tira ambiguidade de qualquer leitor desta lista.
    if (alvo.protocol !== 'https:' || alvo.port !== '' || alvo.username !== '' || alvo.password !== '') return null;
    if (!hosts.includes(alvo.hostname.toLowerCase())) return null;

    const m = String(method || '').toUpperCase();
    for (const rule of GEO_CALLER_RULES) {
        if (rule.method === m && rule.path.test(alvo.pathname)) return rule;
    }
    return null;
}

/**
 * A loja está dentro da fração do rollout? Mesma conta do PDV (`restaurant_id % 100 < percent`).
 * Id ausente, zero, negativo ou fracionário = fora (sem id não há como ser determinístico).
 * Percentual fora de 0–100 (um `1000` digitado sem querer) = DESLIGADO: erro de configuração numa
 * rota de receita falha para o lado em que a requisição sai como sempre, não para o de ligar todas.
 */
export function inRollout(restaurantId: any, percent: number): boolean {
    const id = Number(restaurantId);
    if (!isFinite(id) || id <= 0 || Math.floor(id) !== id) return false;
    const p = Number(percent);
    if (!isFinite(p) || p <= 0 || p > 100) return false;
    return id % 100 < p;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** UUID (versões 1–8, variante RFC 4122). A sessão de captura gera v4; vazio e lixo NÃO passam. */
export function isUuid(valor: any): boolean {
    return typeof valor === 'string' && UUID.test(valor);
}

/**
 * Lê `localStorage.pedirCallerHeader`: `'off'`, `'on'` ou `null`. Storage indisponível (modo privado,
 * webview, cota) devolve `null` — sem a chave, vale o dial.
 */
export function readCallerSwitch(): 'off' | 'on' | null {
    try {
        const v = String(window.localStorage.getItem(GEO_CALLER_SWITCH_KEY) || '').trim().toLowerCase();
        return v === 'off' || v === 'on' ? v : null;
    } catch (e) {
        return null;
    }
}
